/**
 * Reading a condition through a scope.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Resolution is NOT reimplemented here — it delegates to
 * resolveOverrideThreshold, the single place precedence (campaign → country →
 * default) is decided. A second implementation is how a chip in the UI and the
 * engine that rotates traffic end up disagreeing.
 */

import {
  resolveOverrideEntry,
  type TagCompareTo,
  type TagTrafficProvider,
} from '@trafficpulse/shared';
import type {
  TagRuleCampaignThreshold,
  TagRuleCondition,
  TagRuleCountryThreshold,
} from '@/hooks';

import { normScope, scopeTarget, type TagRuleScope } from './scope';

/** Where the number on screen came from. `country` only appears when a campaign
 *  is selected but only its country carries a value — the distinction the user
 *  needs before editing, since editing would create a campaign-level row. */
export type ValueSource = 'own' | 'country' | 'default';

export interface ScopedValue {
  value: number;
  source: ValueSource;
  /** True when the selected level has its own row. */
  overridden: boolean;
  defaultValue: number;
  /** Set when the winning tier COMPARES instead of pinning a number. */
  compareTo?: TagCompareTo;
}

export function countryRowFor(
  c: TagRuleCondition,
  countryKey: string,
): TagRuleCountryThreshold | undefined {
  return (c.countryThresholds ?? []).find(
    (t) => normScope(t.countryKey) === normScope(countryKey),
  );
}

export function campaignRowFor(
  c: TagRuleCondition,
  provider: TagTrafficProvider,
  campaignId: string,
): TagRuleCampaignThreshold | undefined {
  return (c.campaignThresholds ?? []).find(
    (t) => t.provider === provider && normScope(t.campaignId) === normScope(campaignId),
  );
}

/** Does the SELECTED level carry its own row? Distinct from "resolves to a
 *  number": an inherited condition still resolves, just not from here. */
export function hasOwnOverride(c: TagRuleCondition, scope: TagRuleScope): boolean {
  const target = scopeTarget(scope);
  if (target === 'campaign') {
    return campaignRowFor(c, scope.provider!, scope.campaignId!) !== undefined;
  }
  if (target === 'country') return countryRowFor(c, scope.countryKey!) !== undefined;
  return false;
}

function sourceOf(c: TagRuleCondition, scope: TagRuleScope): ValueSource {
  if (hasOwnOverride(c, scope)) return 'own';
  // A campaign with no row of its own still sits inside its country.
  if (scopeTarget(scope) === 'campaign' && scope.countryKey) {
    if (countryRowFor(c, scope.countryKey)) return 'country';
  }
  return 'default';
}

/**
 * What this scope compares against. Both the country and the campaign go into
 * the context, so the number shown is the one the engine would really use.
 */
export function resolveForScope(
  c: TagRuleCondition,
  scope: TagRuleScope,
): ScopedValue {
  const defaultValue = c.value;
  if (scopeTarget(scope) === 'default') {
    return {
      value: defaultValue,
      source: 'default',
      overridden: false,
      defaultValue,
      compareTo: c.compareTo,
    };
  }
  const entry = resolveOverrideEntry({
    value: defaultValue,
    compareTo: c.compareTo,
    countryThresholds: c.countryThresholds,
    campaignThresholds: c.campaignThresholds,
    ctx: {
      countryKey: scope.countryKey,
      ...(scope.provider && scope.campaignId
        ? { campaignId: scope.campaignId, provider: scope.provider }
        : {}),
    },
  });
  const source = sourceOf(c, scope);
  return {
    value: entry.compareTo ? defaultValue : entry.value,
    source,
    overridden: source === 'own',
    defaultValue,
    compareTo: entry.compareTo,
  };
}
